import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../utils/auth';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import API_BASE_URL from '../config';

export default function Drivers() {
  const { isEditDisabled } = useAuth();
  // Helper to format date to Sri Lanka time (GMT+5:30)
  const formatSLTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const offsetDate = new Date(date.getTime() + (330 - date.getTimezoneOffset()) * 60000);
    return offsetDate.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }) + ' (GMT+5:30)';
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editDriver, setEditDriver] = useState(null);
  const [editForm, setEditForm] = useState({
    swipe_driverlicense: '',
    swipe_drivername: '',
    swipe_driverempid: '',
    swipe_drivercardid: '',
    swipe_driverphone: '',
    status: 1
  });
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    swipe_driverlicense: '',
    swipe_drivername: '',
    swipe_driverempid: '',
    swipe_drivercardid: '',
    swipe_driverphone: '',
    status: 1
  });
  const [creating, setCreating] = useState(false);

  const handleEditClick = (driver) => {
    if (isEditDisabled) return;
    setEditDriver(driver);
    setEditForm({
      swipe_driverlicense: driver.swipe_driverlicense || '',
      swipe_drivername: driver.swipe_drivername || '',
      swipe_driverempid: driver.swipe_driverempid || '',
      swipe_drivercardid: driver.swipe_drivercardid || '',
      swipe_driverphone: driver.swipe_driverphone || '',
      status: typeof driver.status === 'number' ? driver.status : 1
    });
    setEditOpen(true);
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleCreateChange = (e) => {
    setCreateForm({ ...createForm, [e.target.name]: e.target.value });
  };

  const handleStatusChange = (e) => {
    setEditForm({ ...editForm, status: e.target.checked ? 1 : 0 });
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/drivers/${editDriver.index}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error('Failed to update driver');
      setDrivers((prev) => prev.map(d => d.index === editDriver.index ? { ...d, ...editForm, updated_at: new Date().toISOString() } : d));
      setEditOpen(false);
      setEditDriver(null);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setEditDriver(null);
  };

  const handleCreateOpen = () => {
    if (isEditDisabled) return;
    setCreateForm({ swipe_driverlicense: '', swipe_drivername: '', swipe_driverempid: '', swipe_drivercardid: '', swipe_driverphone: '', status: 1 });
    setCreateOpen(true);
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
  };

  const handleCreateSave = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/drivers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(createForm),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to create driver');
      }
      const newDriver = await res.json();
      // Prepend new driver so it's visible immediately
      setDrivers((prev) => [newDriver, ...prev]);
      setCreateOpen(false);
    } catch (e) {
      alert(e.message || 'Error creating driver');
    } finally {
      setCreating(false);
    }
  };

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const filteredDrivers = useMemo(() => {
    if (!query) return drivers;
    const q = String(query).toLowerCase();
    return drivers.filter(d => (
      String(d.swipe_drivername || '').toLowerCase().includes(q) ||
      String(d.swipe_driverlicense || '').toLowerCase().includes(q) ||
      String(d.swipe_driverempid || '').toLowerCase().includes(q) ||
      String(d.swipe_drivercardid || '').toLowerCase().includes(q) ||
      String(d.swipe_driverphone || '').toLowerCase().includes(q)
    ));
  }, [drivers, query]);

  useEffect(() => {
    const fetchDrivers = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/drivers`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });
        if (!res.ok) throw new Error('Failed to fetch drivers');
        const data = await res.json();
        setDrivers(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDrivers();
  }, []);

  return (
    <div>
      <Typography variant="h4" gutterBottom>Drivers</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField placeholder="Search drivers" value={query} onChange={(e) => setQuery(e.target.value)} size="small" sx={{ minWidth: 200 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleCreateOpen} disabled={isEditDisabled}>Create New Driver</Button>
        </Box>
      </Box>
      {loading && <Typography>Loading...</Typography>}
      {error && <Typography color="error">{error}</Typography>}
      {!loading && drivers.length > 0 && (
  <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Driver Name</TableCell>
                <TableCell>License</TableCell>
                <TableCell>Emp ID</TableCell>
                <TableCell>Card ID</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDrivers.length > 0 ? filteredDrivers.map((driver,driverIndex) => (
                <TableRow key={driver.index}>
                  <TableCell>{driverIndex+1}</TableCell>
                  <TableCell>{driver.swipe_drivername}</TableCell>
                  <TableCell>{driver.swipe_driverlicense}</TableCell>
                  <TableCell>{driver.swipe_driverempid}</TableCell>
                  <TableCell>{driver.swipe_drivercardid}</TableCell>
                  <TableCell>{driver.swipe_driverphone}</TableCell>
                  <TableCell>
                    {driver.status === 1 ? (
                      <span style={{ color: 'white', backgroundColor: 'green', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>Active</span>
                    ) : (
                      <span style={{ color: 'white', backgroundColor: 'red', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>Inactive</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => handleEditClick(driver)} disabled={isEditDisabled}>Edit</Button>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">No drivers match your search.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Edit Modal - Moved outside table loop for better performance */}
      <Modal open={editOpen} onClose={handleEditClose}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: 24, minWidth: 320 }}>
          <Typography variant="h6" gutterBottom>Edit Driver</Typography>
          <TextField label="Driver Name" name="swipe_drivername" value={editForm.swipe_drivername} onChange={handleEditChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="License" name="swipe_driverlicense" value={editForm.swipe_driverlicense} onChange={handleEditChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="Emp ID" name="swipe_driverempid" value={editForm.swipe_driverempid} onChange={handleEditChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="Card ID" name="swipe_drivercardid" value={editForm.swipe_drivercardid} onChange={handleEditChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="Phone" name="swipe_driverphone" value={editForm.swipe_driverphone} onChange={handleEditChange} fullWidth sx={{ mb: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={editForm.status === 1}
                onChange={handleStatusChange}
                color="success"
              />
            }
            label={editForm.status === 1 ? "Active" : "Inactive"}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleEditClose} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave} disabled={saving || isEditDisabled}>Save</Button>
          </Box>
        </Box>
      </Modal>

      {/* Create Modal */}
      <Modal open={createOpen} onClose={handleCreateClose}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'background.paper', p: 4, borderRadius: 2, boxShadow: 24, minWidth: 320 }}>
          <Typography variant="h6" gutterBottom>Create Driver</Typography>
          <TextField label="Driver Name" name="swipe_drivername" value={createForm.swipe_drivername} onChange={handleCreateChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="License" name="swipe_driverlicense" value={createForm.swipe_driverlicense} onChange={handleCreateChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="Emp ID" name="swipe_driverempid" value={createForm.swipe_driverempid} onChange={handleCreateChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="Card ID" name="swipe_drivercardid" value={createForm.swipe_drivercardid} onChange={handleCreateChange} fullWidth sx={{ mb: 2 }} />
          <TextField label="Phone" name="swipe_driverphone" value={createForm.swipe_driverphone} onChange={handleCreateChange} fullWidth sx={{ mb: 2 }} />
          <FormControlLabel
            control={
              <Switch
                checked={createForm.status === 1}
                onChange={(e) => setCreateForm({ ...createForm, status: e.target.checked ? 1 : 0 })}
                color="success"
              />
            }
            label={createForm.status === 1 ? "Active" : "Inactive"}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCreateClose} disabled={creating}>Cancel</Button>
            <Button variant="contained" onClick={handleCreateSave} disabled={creating || isEditDisabled}>Create</Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
